import { IAccount } from "../Accounting/store"
import { IAddress } from "./../Addresses"
import { IContact } from "./../Contacts"
import { IHotelGroup } from "./../HotelGroups"
import { IMealPlan } from "./../MealPlans"
import { IRoomType } from "./../RoomTypes"
import { THotelNote } from "../HotelNotes/store"
import { TMedia } from "./../Media"
import { TTripDestination } from "../TripDestinations"
import { IUser } from "../Auth"
import useSWR from "swr"
import {
	ISimpleListResponse,
	IListResponse,
	useXHR,
	XHRInstance,
} from "@sembark-travel/xhr"
import { parseDate } from "@sembark-travel/datetime-utils"
import { ISupplier } from "../Supplier"
import { IPaymentPreference } from "../SupplierPaymentPreferences"
import { isTruthy } from "@sembark-travel/ui-form"

export type TDateInterval = {
	start_date: Date
	end_date: Date
}

export type TGroupedRoomInterval = {
	black_out_date_intervals: TDateInterval[]
	room_types: {
		name: string
		value: number
	}[]
	blackout: boolean | 1 | 0
}

export type TSoldoutRoomInterval = {
	black_out_date_intervals: TDateInterval[]
	room_types: {
		name: string
		value: number
	}[]
	blackout: false | 0
}

export type TBlackoutRoomInterval = {
	black_out_date_intervals: TDateInterval[]
	room_types: {
		name: string
		value: number
	}[]
	blackout: true | 1
}

export interface IPrice {
	id: number
	hotel_id: number
	base_price: number
	persons: number
	adult_with_extra_bed_price: number
	child_with_extra_bed_price: number
	child_without_extra_bed_price: number
	start_date: string
	end_date: string
	meal_plan_id: number
	room_type_id: number
	meal_plan: IMealPlan
	room_type: IRoomType
}

export interface IHotelRoomType extends IRoomType {
	allowed_extra_beds: number
	allowed_adults_with_extra_bed: number
	allowed_children_with_extra_bed: number
	allowed_children_without_extra_bed?: number
	no_of_rooms?: number
	black_out_date_intervals: Array<{
		start_date: string
		end_date: string
		id: string
		name: string
		stop_sales: boolean | 1 | 0
	}>
}
export type IHotelMealPlan = IMealPlan

export type IHotelAddress = IAddress

export type THotelLocation = {
	id: number
	name: string
	short_name: string
}
export interface IHotel {
	id: number
	name: string
	stars?: string | null
	no_of_stars?: number | null
	stars_string?: string | null
	extra_bed_child_age_start: number
	extra_bed_child_age_end: number
	meal_plans: IHotelMealPlan[]
	entry_type: "manually" | "by_sheet" | "by_preload"
	room_types: IHotelRoomType[]
	address: IHotelAddress
	prices?: IPrice[]
	contact?: IContact
	contacts?: IContact[]
	notes?: THotelNote[]
	global_notes?: THotelNote[] // append by frontend on selectHoles
	payment_preference?: IPaymentPreference
	checkin_at_local?: string
	checkin_at: string
	checkout_at_local?: string
	checkout_at: string
	accounting_accounts?: Array<IAccount>
	group?: IHotelGroup
	latest_media?: TMedia | null
	thumb_image_url?: string | null
	trip_destinations?: Array<TTripDestination>
	created_at: string
	created_by?: IUser
	updated_at: string | null
	updated_by?: IUser
	deleted_at: string | null
	deleted_by?: IUser
	url?: string
	location: THotelLocation
	rate_expiry_date: string | null
	rate_expiry_date_local: string | null
	rate_uploaded_at: string | null
	rate_uploaded_by: IUser | null
	direct_hotel_booking_exists?: boolean
	same_black_out_date_intervals_for_all_room_types: boolean
	suppliers?: ISupplier[]
	same_sold_out_date_intervals_for_all_room_types: boolean
}

export function useHotel(hotelId: string | number) {
	const xhr = useXHR()
	const {
		data,
		mutate: revalidate,
		isValidating,
		error,
	} = useSWR(`/api/hotels/${hotelId}`, () =>
		xhr
			.get<{ data: IHotel }>(`/hotels/${hotelId}`, {
				params: {
					include: "with_suppliers",
				},
			})
			.then((resp) => resp.data.data)
	)
	const isFetching = !data && !error
	const isRefreshing = isValidating
	return {
		hotel: data,
		fetchHotel: revalidate,
		isFetching,
		isRefreshing,
		error,
	}
}

interface ISimpleListResponseWithNotes<Item> extends ISimpleListResponse<Item> {
	meta: ISimpleListResponse<Item>["meta"] & {
		general_hotel_notes?: THotelNote[]
	}
}
export function hotelsXHR(xhr: XHRInstance) {
	return {
		async getHotels(
			params?: Record<string, unknown>
		): Promise<ISimpleListResponseWithNotes<IHotel>> {
			return xhr
				.get("/hotels", { params: { ...params, pagination: "simple" } })
				.then((resp) => resp.data)
		},
		async getHotelsWithTotal(params?: unknown): Promise<IListResponse<IHotel>> {
			return xhr.get("/hotels", { params }).then((resp) => resp.data)
		},
		async getHotelStarCategories(params?: unknown) {
			return xhr
				.get<{ data: Array<string> }>("/hotel-star-categories", { params })
				.then((resp) => resp.data.data.map((d) => ({ id: d, name: d })))
		},
		async getHotelLocations(params?: unknown) {
			return xhr
				.get<
					ISimpleListResponse<THotelLocation>
				>("/hotel-locations", { params })
				.then((resp) => resp.data.data)
		},
		async getHotelContacts(params?: unknown) {
			return xhr
				.get<ISimpleListResponse<IContact>>("/contacts", { params })
				.then((resp) => resp.data.data)
		},
		async createContact(
			id: string | number,
			contactData: object
		): Promise<IContact> {
			return xhr
				.post(`/hotel-contacts`, {
					hotel_id: id,
					...contactData,
				})
				.then((resp) => resp.data.data)
		},
	}
}

function isSoldOutInterval(interval: { stop_sales?: number | boolean }) {
	return interval.stop_sales === 1 || interval.stop_sales === true
}

function isBlackoutInterval(interval: { stop_sales?: number | boolean }) {
	return !isSoldOutInterval(interval)
}

function buildSimpleIntervals(
	roomTypes: IHotel["room_types"],
	matches: (interval: { stop_sales?: number | boolean }) => boolean
): TDateInterval[] {
	const intervalsMap = new Map<string, TDateInterval>()

	roomTypes?.forEach((room) => {
		room.black_out_date_intervals?.forEach((interval) => {
			if (!matches(interval)) return

			const key = `${interval.start_date}_${interval.end_date}`
			if (!intervalsMap.has(key)) {
				intervalsMap.set(key, {
					start_date: parseDate(interval.start_date),
					end_date: parseDate(interval.end_date),
				})
			}
		})
	})

	return Array.from(intervalsMap.values())
}

function buildGroupedRoomIntervals(
	roomTypes: IHotel["room_types"],
	matches: (interval: { stop_sales?: number | boolean }) => boolean,
	blackoutFlag: 0 | 1
): TGroupedRoomInterval[] {
	const intervalMap = new Map<string, TGroupedRoomInterval>()

	roomTypes?.forEach((room) => {
		room.black_out_date_intervals?.forEach((interval) => {
			if (!matches(interval)) return

			const intervalKey = `${interval.start_date}_${interval.end_date}`
			if (!intervalMap.has(intervalKey)) {
				intervalMap.set(intervalKey, {
					black_out_date_intervals: [
						{
							start_date: parseDate(interval.start_date),
							end_date: parseDate(interval.end_date),
						},
					],
					room_types: [],
					blackout: isTruthy(blackoutFlag),
				})
			}

			const group = intervalMap.get(intervalKey)
			if (!group) return

			if (!group.room_types.some((r) => r.value === room.id)) {
				group.room_types.push({
					name: room.name,
					value: room.id,
				})
			}
		})
	})

	const finalMap = new Map<string, TGroupedRoomInterval>()
	Array.from(intervalMap.values()).forEach((group) => {
		const sortedRoomIds = group.room_types
			.map((r) => r.value)
			.sort((a, b) => a - b)
			.join("_")

		const finalKey = `${sortedRoomIds}_${group.blackout}`
		const existing = finalMap.get(finalKey)
		if (existing) {
			existing.black_out_date_intervals.push(...group.black_out_date_intervals)
		} else {
			finalMap.set(finalKey, { ...group })
		}
	})

	return Array.from(finalMap.values())
}

export function formatClosedDates(
	roomTypes: IHotel["room_types"],
	same_black_out_date_intervals_for_all_room_types: boolean,
	same_sold_out_date_intervals_for_all_room_types: boolean
) {
	const result: {
		stop_sale_date_intervals?: TDateInterval[]
		blackout_date_intervals?: TDateInterval[]
		stop_sale_date_for_room_types?: TSoldoutRoomInterval[]
		blackout_date_for_room_types?: TBlackoutRoomInterval[]
	} = {}

	// ---------- Soldout ----------
	if (same_sold_out_date_intervals_for_all_room_types) {
		result.stop_sale_date_intervals = buildSimpleIntervals(
			roomTypes,
			isSoldOutInterval
		)
	} else {
		const soldoutGroups = buildGroupedRoomIntervals(
			roomTypes,
			isSoldOutInterval,
			0
		) as TSoldoutRoomInterval[]

		if (soldoutGroups?.length > 0) {
			result.stop_sale_date_for_room_types = soldoutGroups
		}
	}

	// ---------- Blackout ----------
	if (same_black_out_date_intervals_for_all_room_types) {
		result.blackout_date_intervals = buildSimpleIntervals(
			roomTypes,
			isBlackoutInterval
		)
	} else {
		const blackoutGroups = buildGroupedRoomIntervals(
			roomTypes,
			isBlackoutInterval,
			1
		) as TBlackoutRoomInterval[]

		if (blackoutGroups?.length > 0) {
			result.blackout_date_for_room_types = blackoutGroups
		}
	}

	return result
}

type GetGlobalNotesForHotelDestinationParams = {
	hotelDestination?: TTripDestination[]
	generalNotes?: THotelNote[]
}

export function getGlobalNotesForHotelDestination({
	hotelDestination,
	generalNotes,
}: GetGlobalNotesForHotelDestinationParams) {
	if (!generalNotes?.length) {
		return generalNotes
	}

	const destinationIds = new Set(
		hotelDestination?.map((destination) => destination.id) || []
	)
	return generalNotes.filter((note) => {
		//here if tripDestinations is empty it means that hotel is associated with all destinations
		if (!hotelDestination?.length || !note?.destinations?.length) {
			return true
		}

		return note.destinations.some((destination) =>
			destinationIds.has(destination.id)
		)
	})
}
